import React, { useState, useEffect, useRef, useCallback } from 'react';
import './PostingTeamUp.css';
import logo from '../assets/logo.png';

import { db, auth } from '../firebase';
import { collection, addDoc, serverTimestamp, doc, setDoc, arrayUnion } from 'firebase/firestore';
import { uploadMultipleImages } from '../cloudinaryService';
import imageCompression from 'browser-image-compression';

import AdResultModal from './AdResultModal';
import TeamUpStepContent from './TeamUpStepContent';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faCheck } from '@fortawesome/free-solid-svg-icons';

// ─── Static config ──────────────────────────────────────────────────────────

const STEP_KEYS = ['about', 'team', 'flat', 'lifestyle', 'contact'];
const MAX_PREFERRED_REGIONS = 4;
const MAX_FLAT_PHOTOS = 5;

function PostingTeamUp({ currentLanguage, getLocalizedText }) {
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState('forward');
  const [touchedSteps, setTouchedSteps] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [compressionStatus, setCompressionStatus] = useState(null);
  const [adResult, setAdResult] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const [isCityDropdownOpen, setIsCityDropdownOpen] = useState(false);
  const [isRegionDropdownOpen, setIsRegionDropdownOpen] = useState(false);
  const citySelectorRef = useRef(null);
  const regionSelectorRef = useRef(null);
  const contentScrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const flatPhotosInputRef = useRef(null);
  const [flatPhotosCompressing, setFlatPhotosCompressing] = useState(false);

  const [formData, setFormData] = useState({
    // Step 1 — About You
    first_name: '',
    second_name: '',
    profile_photo: null,
    profile_photo_preview: null,
    gender: '',
    age: '',
    bio: '',
    // Step 2 — Team
    desired_team_size: '',
    current_team_size: '',
    has_flat: '', // 'yes' | 'no'
    // Step 3a — Flat Details (has_flat === 'yes')
    city: '',
    region: '',
    bedrooms: '',
    bathrooms: '',
    monthly_rent: '',
    refundable_insurance: '',
    move_in_date: '',
    flat_description: '',
    open_to_other_flat: '',
    flat_photos: [], // optional, up to MAX_FLAT_PHOTOS compressed File objects
    flat_photos_previews: [],
    // Step 3b — Looking For (has_flat === 'no')
    preferred_city: '',
    preferred_regions: [], // up to MAX_PREFERRED_REGIONS
    budget_per_person: '',
    preferred_move_in_date: '',
    looking_for_description: '',
    // Step 4 — Lifestyle
    lifestyle: [],
    // Step 5 — Contact
    contact_phone: '',
    contact_whatsapp: '',
  });

  useEffect(() => {
    document.documentElement.setAttribute('dir', currentLanguage === 'ar' ? 'rtl' : 'ltr');
    document.documentElement.lang = currentLanguage;
  }, [currentLanguage]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        citySelectorRef.current && !citySelectorRef.current.contains(event.target) &&
        regionSelectorRef.current && !regionSelectorRef.current.contains(event.target)
      ) {
        setIsCityDropdownOpen(false);
        setIsRegionDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    if (contentScrollRef.current) {
      contentScrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [step]);

  const setField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleInput = (e) => {
    const { name, value } = e.target;
    if (name === 'first_name' || name === 'second_name') {
      const cleaned = value.replace(/[0-9٠-٩۰-۹]/g, '').slice(0, 11);
      setField(name, cleaned);
      return;
    }
    if (name === 'contact_phone' || name === 'contact_whatsapp') {
      if (/^\d*$/.test(value)) setField(name, value);
      return;
    }
    if (name === 'desired_team_size' || name === 'current_team_size' || name === 'age' ||
        name === 'bedrooms' || name === 'bathrooms' || name === 'monthly_rent' || name === 'refundable_insurance' || name === 'budget_per_person') {
      if (/^\d*$/.test(value)) setField(name, value);
      return;
    }
    setField(name, value);
  };

  // ── Photo upload ──────────────────────────────────────────────────────────

  const compressAndSetPhoto = async (file) => {
    if (!file) return;
    try {
      setCompressionStatus('compressing');
      const compressed = await imageCompression(file, { maxSizeMB: 1, maxWidthOrHeight: 1600, useWebWorker: true });
      const preview = URL.createObjectURL(compressed);
      setFormData((prev) => ({ ...prev, profile_photo: compressed, profile_photo_preview: preview }));
    } catch (error) {
      alert(getLocalizedText('خطأ في ضغط الصورة', 'Error compressing image'));
    } finally {
      setCompressionStatus(null);
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files && e.target.files[0];
    compressAndSetPhoto(file);
  };

  const handleRemovePhoto = () => {
    setFormData((prev) => ({ ...prev, profile_photo: null, profile_photo_preview: null }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) compressAndSetPhoto(file);
  };

  // ── Flat photos (optional, up to MAX_FLAT_PHOTOS) ───────────────────────────

  const compressAndAddFlatPhotos = async (files) => {
    if (!files || files.length === 0) return;
    const remainingSlots = MAX_FLAT_PHOTOS - formData.flat_photos.length;
    if (remainingSlots <= 0) return;
    const filesToAdd = Array.from(files)
      .filter((f) => f.type.startsWith('image/'))
      .slice(0, remainingSlots);
    if (filesToAdd.length === 0) return;
    try {
      setFlatPhotosCompressing(true);

      const MAX_INPUT_MB = 25;
      const results = await Promise.allSettled(
        filesToAdd.map((file) => {
          if (file.size > MAX_INPUT_MB * 1024 * 1024) {
            return Promise.reject(new Error(`${file.name} too large`));
          }
          return imageCompression(file, { maxSizeMB: 1, maxWidthOrHeight: 1600, useWebWorker: true });
        })
      );

      const compressedFiles = [];
      let failedCount = 0;
      results.forEach((r) => {
        if (r.status === 'fulfilled') {
          compressedFiles.push(r.value);
        } else {
          failedCount += 1;
        }
      });

      if (compressedFiles.length > 0) {
        const previews = compressedFiles.map((file) => URL.createObjectURL(file));
        setFormData((prev) => ({
          ...prev,
          flat_photos: [...prev.flat_photos, ...compressedFiles],
          flat_photos_previews: [...prev.flat_photos_previews, ...previews],
        }));
      }

      if (failedCount > 0) {
        alert(getLocalizedText(
          `تعذر ضغط ${failedCount} صورة، تم رفع الباقي بنجاح`,
          `Couldn't compress ${failedCount} photo(s), the rest were added successfully`
        ));
      }
    } catch (error) {
      alert(getLocalizedText('خطأ في ضغط الصور', 'Error compressing images'));
    } finally {
      setFlatPhotosCompressing(false);
    }
  };

  const handleFlatPhotosChange = (e) => {
    compressAndAddFlatPhotos(e.target.files);
    if (flatPhotosInputRef.current) flatPhotosInputRef.current.value = '';
  };

  const handleFlatPhotosDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    compressAndAddFlatPhotos(e.dataTransfer.files);
  };

  const handleRemoveFlatPhoto = (index) => {
    setFormData((prev) => ({
      ...prev,
      flat_photos: prev.flat_photos.filter((_, i) => i !== index),
      flat_photos_previews: prev.flat_photos_previews.filter((_, i) => i !== index),
    }));
  };

  // ── Location selects ───────────────────────────────────────────────────────

  const handleCitySelect = (field, city) => {
    const regionField = field === 'city' ? 'region' : 'preferred_regions';
    const clearedRegionValue = regionField === 'preferred_regions' ? [] : '';
    setFormData((prev) => ({ ...prev, [field]: city.value, [regionField]: clearedRegionValue }));
    setIsCityDropdownOpen(false);
  };

  const handleRegionSelect = (field, region) => {
    setField(field, region.value);
    setIsRegionDropdownOpen(false);
  };

  // Toggle a region in/out of a multi-select region field (e.g. preferred_regions),
  // capped at MAX_PREFERRED_REGIONS. Dropdown stays open so more picks can follow.
  const handleRegionToggle = (field, region) => {
    setFormData((prev) => {
      const current = prev[field] || [];
      if (current.includes(region.value)) {
        return { ...prev, [field]: current.filter((v) => v !== region.value) };
      }
      if (current.length >= MAX_PREFERRED_REGIONS) return prev;
      return { ...prev, [field]: [...current, region.value] };
    });
  };

  // ── Lifestyle toggle ────────────────────────────────────────────────────────

  const toggleLifestyle = (value) => {
    setFormData((prev) => {
      const current = prev.lifestyle || [];
      return current.includes(value)
        ? { ...prev, lifestyle: current.filter((v) => v !== value) }
        : { ...prev, lifestyle: [...current, value] };
    });
  };

  // ── Validation ───────────────────────────────────────────────────────────

  const flatChosen = formData.has_flat === 'yes';
  const lookingChosen = formData.has_flat === 'no';

  const isStepValid = useCallback((stepIndex) => {
    switch (stepIndex) {
      case 0:
        return !!(formData.first_name.trim() && formData.second_name.trim() && formData.profile_photo && formData.gender && formData.age && formData.bio.trim().length >= 20);
      case 1:
        return !!(formData.desired_team_size && formData.current_team_size && formData.has_flat);
      case 2:
        if (flatChosen) {
          return !!(
            formData.city && formData.region && formData.bedrooms && formData.bathrooms &&
            formData.monthly_rent && formData.move_in_date &&
            formData.flat_description.trim().length >= 20 && formData.open_to_other_flat
          );
        }
        if (lookingChosen) {
          return !!(
            formData.preferred_city && formData.preferred_regions.length > 0 && formData.budget_per_person &&
            formData.preferred_move_in_date && formData.looking_for_description.trim().length >= 20
          );
        }
        return false;
      case 3:
        return formData.lifestyle.length > 0;
      case 4: {
        const phoneRegex = /^\d{11}$/;
        return phoneRegex.test(formData.contact_phone) && phoneRegex.test(formData.contact_whatsapp);
      }
      default:
        return false;
    }
  }, [formData, flatChosen, lookingChosen]);

  const isFieldValid = (field) => {
    switch (field) {
      case 'first_name': return formData.first_name.trim().length > 0;
      case 'second_name': return formData.second_name.trim().length > 0;
      case 'profile_photo': return !!formData.profile_photo;
      case 'gender': return !!formData.gender;
      case 'age': return !!formData.age;
      case 'bio': return formData.bio.trim().length >= 20;
      case 'desired_team_size': return !!formData.desired_team_size;
      case 'current_team_size': return !!formData.current_team_size;
      case 'has_flat': return !!formData.has_flat;
      case 'city': return !!formData.city;
      case 'region': return !!formData.region;
      case 'bedrooms': return !!formData.bedrooms;
      case 'bathrooms': return !!formData.bathrooms;
      case 'monthly_rent': return !!formData.monthly_rent;
      case 'refundable_insurance': return true;
      case 'move_in_date': return !!formData.move_in_date;
      case 'flat_description': return formData.flat_description.trim().length >= 20;
      case 'open_to_other_flat': return !!formData.open_to_other_flat;
      case 'preferred_city': return !!formData.preferred_city;
      case 'preferred_regions': return formData.preferred_regions.length > 0;
      case 'budget_per_person': return !!formData.budget_per_person;
      case 'preferred_move_in_date': return !!formData.preferred_move_in_date;
      case 'looking_for_description': return formData.looking_for_description.trim().length >= 20;
      case 'contact_phone': return /^\d{11}$/.test(formData.contact_phone);
      case 'contact_whatsapp': return /^\d{11}$/.test(formData.contact_whatsapp);
      default: return false;
    }
  };

  const totalSteps = STEP_KEYS.length;
  const progressPercent = Math.round(((step + 1) / totalSteps) * 100);

  const stepLabels = [
    getLocalizedText('عنك', 'About You'),
    getLocalizedText('الفريق', 'Team'),
    flatChosen
      ? getLocalizedText('تفاصيل الشقة', 'Flat Details')
      : getLocalizedText('أبحث عن', 'Looking For'),
    getLocalizedText('نمط الحياة', 'Lifestyle & Rules'),
    getLocalizedText('بيانات التواصل', 'Contact info'),
  ];

  const goNext = () => {
    if (!isStepValid(step)) {
      setTouchedSteps((prev) => ({ ...prev, [step]: true }));
      return;
    }
    if (step < totalSteps - 1) {
      setDirection('forward');
      setStep((s) => s + 1);
    }
  };

  const goBack = () => {
    if (step > 0) {
      setDirection('backward');
      setStep((s) => s - 1);
    }
  };

  const goToStep = (target) => {
    // only allow navigating to already-completed steps
    if (target < step) {
      setDirection('backward');
      setStep(target);
    }
  };

  // ── Submit ─────────────────────────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!auth.currentUser) {
      alert(getLocalizedText('يجب تسجيل الدخول أولاً', 'Please login first'));
      return;
    }
    if (!isStepValid(4)) {
      setTouchedSteps((prev) => ({ ...prev, 4: true }));
      return;
    }

    setIsSubmitting(true);
    try {
      let photoUrl = null;
      if (formData.profile_photo) {
        const [uploadedUrl] = await uploadMultipleImages([formData.profile_photo]);
        photoUrl = uploadedUrl;
      }

      let flatPhotoUrls = [];
      if (flatChosen && formData.flat_photos.length > 0) {
        flatPhotoUrls = await uploadMultipleImages(formData.flat_photos);
      }

      const baseData = {
        userId: auth.currentUser.uid,
        userEmail: auth.currentUser.email,
        type: 'team_up',
        personal_name: `${formData.first_name.trim()} ${formData.second_name.trim()}`.trim(),
        profile_photo: photoUrl,
        gender: formData.gender,
        age: formData.age ? parseInt(formData.age, 10) : null,
        bio: formData.bio.trim(),
        desired_team_size: parseInt(formData.desired_team_size, 10),
        current_team_size: parseInt(formData.current_team_size, 10),
        has_flat: formData.has_flat,
        lifestyle: formData.lifestyle,
        contact_phone: formData.contact_phone,
        contact_whatsapp: formData.contact_whatsapp,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: 'active',
      };

      const flatData = flatChosen
        ? {
            city: formData.city,
            region: formData.region,
            bedrooms: parseInt(formData.bedrooms, 10),
            bathrooms: parseInt(formData.bathrooms, 10),
            monthly_rent: parseFloat(formData.monthly_rent),
            refundable_insurance: formData.refundable_insurance ? parseFloat(formData.refundable_insurance) : null,
            move_in_date: formData.move_in_date,
            flat_description: formData.flat_description.trim(),
            open_to_other_flat: formData.open_to_other_flat,
            flat_photos: flatPhotoUrls,
          }
        : {
            preferred_city: formData.preferred_city,
            preferred_regions: formData.preferred_regions,
            budget_per_person: parseFloat(formData.budget_per_person),
            preferred_move_in_date: formData.preferred_move_in_date,
            looking_for_description: formData.looking_for_description.trim(),
          };

      const teamUpData = { ...baseData, ...flatData };

      const docRef = await addDoc(collection(db, 'team_up_ads'), teamUpData);

      const userDocRef = doc(db, 'verified_users', auth.currentUser.uid);
      await setDoc(userDocRef, { posted_ads_id: arrayUnion(docRef.id) }, { merge: true });

      setAdResult({ success: true });
    } catch (error) {
      console.error('Error publishing team up ad:', error);
      setAdResult({ success: false, message: error.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Props bundle passed down to the step-content component ────────────────

  const stepContentProps = {
    step,
    flatChosen,
    getLocalizedText,
    formData,
    handleInput,
    setField,
    isFieldValid,
    touchedSteps,
    toggleLifestyle,
    // photo upload
    isDragging,
    setIsDragging,
    handleDrop,
    handlePhotoChange,
    handleRemovePhoto,
    fileInputRef,
    compressionStatus,
    // flat photos (optional, up to MAX_FLAT_PHOTOS)
    flatPhotosInputRef,
    flatPhotosCompressing,
    handleFlatPhotosChange,
    handleFlatPhotosDrop,
    handleRemoveFlatPhoto,
    MAX_FLAT_PHOTOS,
    // location dropdowns
    isCityDropdownOpen,
    setIsCityDropdownOpen,
    isRegionDropdownOpen,
    setIsRegionDropdownOpen,
    citySelectorRef,
    regionSelectorRef,
    handleCitySelect,
    handleRegionSelect,
    handleRegionToggle,
  };

  return (
    <div className="tu-page">
      <div className="tu-container">
        <div className="tu-header">
          <h2>{getLocalizedText('كون فريقاً للسكن', 'Create your Team-Up')}</h2>
          <span className="tu-close-button" onClick={() => (window.location.href = '/')}>×</span>
        </div>

        {/* Progress stepper */}
        <div className="tu-stepper-wrap">
          <div className="tu-stepper-meta">
            <span>{getLocalizedText(`الخطوة ${step + 1} من ${totalSteps}`, `Step ${step + 1} of ${totalSteps}`)}</span>
            
          </div>
          <div className="tu-stepper">
            {STEP_KEYS.map((key, i) => {
              const isCompleted = i < step;
              const isCurrent = i === step;
              const isClickable = i < step;
              return (
                <React.Fragment key={key}>
                  <div
                    className={`tu-stepper-node ${isCompleted ? 'is-complete' : ''} ${isCurrent ? 'is-current' : ''} ${isClickable ? 'is-clickable' : ''}`}
                    onClick={() => isClickable && goToStep(i)}
                  >
                    <span className="tu-stepper-dot">
                      {isCompleted ? <FontAwesomeIcon icon={faCheck} /> : i + 1}
                    </span>
                    <span className="tu-stepper-label">{stepLabels[i]}</span>
                  </div>
                  {i < STEP_KEYS.length - 1 && (
                    <div className={`tu-stepper-connector ${i < step ? 'is-filled' : ''}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="tu-form">
          <div className="tu-content" ref={contentScrollRef}>
            <div key={step} className={`tu-step-panel tu-anim-${direction}`}>
              <TeamUpStepContent {...stepContentProps} />
            </div>
          </div>

          <div className="tu-bottom-nav">
            <button
              type="button"
              className="tu-btn tu-btn-ghost"
              onClick={goBack}
              disabled={step === 0}
              style={{ visibility: step === 0 ? 'hidden' : 'visible' }}
            >
              {getLocalizedText('السابق', 'Back')}
            </button>

            {step < totalSteps - 1 ? (
              <button
                type="button"
                className="tu-btn tu-btn-primary"
                onClick={goNext}
                disabled={!isStepValid(step)}
              >
                {getLocalizedText('التالي', 'Next')}
              </button>
            ) : (
              <button
                type="submit"
                className="tu-btn tu-btn-primary"
                disabled={!isStepValid(4) || isSubmitting}
              >
                {isSubmitting
                  ? getLocalizedText('جاري النشر...', 'Publishing...')
                  : getLocalizedText('نشر الطلب', 'Publish Request')}
              </button>
            )}
          </div>
        </form>
      </div>

      {adResult && (
        <AdResultModal
          isSuccess={adResult.success}
          message={adResult.message}
          currentLanguage={currentLanguage}
          getLocalizedText={getLocalizedText}
          onClose={() => {
            const wasSuccess = adResult.success;
            setAdResult(null);
            if (wasSuccess) window.location.href = '/';
          }}
        />
      )}
    </div>
  );
}

export default PostingTeamUp;